import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { Dictionary } from "src/app/store/models/common_types";
import {
  IndustryDetails,
  SectorDetails,
  SectorIndustry,
  EntityGroup,
  Cluster,
  OtherSelectedDetails,
} from "src/app/store/models/industry-page.model";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { faChevronRight, faCheck } from "@fortawesome/pro-light-svg-icons";
import { INDUSTRY_PAGE_INFO } from "src/app/sectors-and-industries-page/sector-and-industry-page-constants";
import { SectorsAndIndustriesPageDataService } from "../sectors-and-industries-page-data-service";
import {
  SetReloadAll,
  SetClusterData,
  ChangeSectorIndustryAndSetClusterData,
} from "src/app/store/actions/sector-industry.actions";
import { MegaMenuService } from "../mega-menu.service";
import { AppService } from "src/app/app.service";

/**
 * Industry Mega Menu component
 * This component is responsible to provide sector and industry navigation on Industry Page
 */
@Component({
  selector: "app-industry-mega-menu",
  templateUrl: "./industry-mega-menu.component.html",
  styleUrls: ["./industry-mega-menu.component.scss"],
})
export class IndustryMegaMenuComponent implements OnInit {
  @Input() pageShortName: string = INDUSTRY_PAGE_INFO.kpis.shortName;
  @Input() isMegaMenuVisible = false;
  @Output() close: EventEmitter<void> = new EventEmitter();
  //subscribers
  sectorIndustryStoreSubscriber: Subscription;
  sectorIndustryStoreCompaniesDataSubscriber: Subscription;
  companyFilterSubscriber: Subscription;
  routeParamSubscriber: Subscription;
  userIndustrySettingsSubscriber: Subscription;

  //instance variables
  sectors: Array<SectorDetails> = [];
  sectorToIndustries: Dictionary<Array<IndustryDetails>> = {};
  selectedSector: string;
  activeSector: string;
  selectedIndustry: string;
  initialSelectedIndustry: string;
  selectedIndustryCount: number;
  // geographies: Array<any>;
  // selectedGeographies: Array<any>;
  // commonCountryIds: Dictionary<any>;
  // regionToCountryMap: Dictionary<any>;
  marketCaps: Array<EntityGroup>;
  clusters: Array<Cluster>;
  otherGeographies: Array<EntityGroup>;
  selectedCluster: number;
  initialSelectedCluser: number;
  selectedMarketCaps: Array<number>;
  popularSelected: Set<number>;
  otherSelected: OtherSelectedDetails;
  selectedClusterText = "";
  selectedGeographyText = "";
  selectedMarketCapText = "";
  selectedCompanyCount: number;
  marketCapExpandedKeys: Array<number> = [-1];
  popularGeography: Array<EntityGroup>;
  allGeography: EntityGroup;
  error: boolean;
  isMegaMenuReady = false;
  disableApply = true;
  // columns: Array<number> = [];
  // rows: Array<number> = [];
  isAlreadyScrolled = false;
  //icons
  faChevronRight = faChevronRight;
  faCheck = faCheck;
  faExclamationTriangle = faExclamationTriangle;
  latestClusterDetails;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store<AppState>,
    private appService: AppService,
    private dataService: SectorsAndIndustriesPageDataService,
    private megaMenuService: MegaMenuService,
  ) {}

  ngOnInit() {
    this.sectorIndustryStoreSubscriber = this.store
      .select(stateGetter("sectorIndustry"))
      .subscribe((_sectorIndustryState: SectorIndustry) => {
        if (_sectorIndustryState.hasData) {
          this.sectors = Object.keys(_sectorIndustryState.sectorMap).map(
            (_sector) => {
              const _industries = _sectorIndustryState.sectorMap[
                _sector
              ].industryIds.map((_ind) => {
                return _sectorIndustryState.industryMap[_ind];
              });
              this.sectorToIndustries[_sector] = _industries;
              return _sectorIndustryState.sectorMap[_sector];
            },
          );
          this.selectedSector = _sectorIndustryState.selectedSectorId;
          this.activeSector = this.selectedSector;
          this.initialSelectedIndustry =
            _sectorIndustryState.selectedIndustryId;
          this.selectedIndustry = _sectorIndustryState.selectedIndustryId;
          this.selectedIndustryCount =
            this.sectorToIndustries[this.activeSector].length;

          if (_sectorIndustryState.hasCompaniesData) {
            this.error = _sectorIndustryState.error;
            this.latestClusterDetails = { ..._sectorIndustryState };
            this.allGeography = _sectorIndustryState.allGeography
              ? this.appService.deepClone(_sectorIndustryState.allGeography)
              : _sectorIndustryState.allGeography;
            this.popularGeography = _sectorIndustryState.popularGeography
              ? this.appService.deepClone(_sectorIndustryState.popularGeography)
              : _sectorIndustryState.popularGeography;
            this.otherGeographies = _sectorIndustryState.otherGeographies
              ? this.appService.deepClone(_sectorIndustryState.otherGeographies)
              : _sectorIndustryState.otherGeographies;
            this.popularSelected = _sectorIndustryState.popularSelected
              ? new Set(_sectorIndustryState.popularSelected)
              : _sectorIndustryState.popularSelected;
            this.otherSelected = _sectorIndustryState.otherSelected
              ? {
                  countries: new Set(
                    _sectorIndustryState.otherSelected.countries,
                  ),
                  regions: new Set(_sectorIndustryState.otherSelected.regions),
                }
              : _sectorIndustryState.otherSelected;
            this.marketCaps = _sectorIndustryState.marketCaps
              ? this.appService.deepClone(_sectorIndustryState.marketCaps)
              : _sectorIndustryState.marketCaps;
            this.selectedMarketCaps = _sectorIndustryState.selectedMarketCaps
              ? this.appService.deepClone(
                  _sectorIndustryState.selectedMarketCaps,
                )
              : _sectorIndustryState.selectedMarketCaps;
            this.clusters = this.appService.deepClone(
              _sectorIndustryState.clusters,
            );
            this.selectedCompanyCount =
              _sectorIndustryState.selectedCompanyCount;
            this.isMegaMenuReady = true;
            this.disableApply = false;
          }
          this.selectedCluster = _sectorIndustryState.selectedCluster;
          this.initialSelectedCluser = _sectorIndustryState.selectedCluster;
        } else {
          // nothing to do
        }
      });

    this.sectorIndustryStoreCompaniesDataSubscriber = this.store
      .select(stateGetter("sectorIndustry", "hasCompaniesData"))
      .subscribe((_hasCompaniesData: boolean) => {
        if (this.isMegaMenuVisible && !_hasCompaniesData) {
          this.isMegaMenuReady = false;
          this.disableApply = true;
          let industryId = this.selectedIndustry;
          if (!industryId) {
            const pathUrl = window.location.pathname.split("/");
            if (pathUrl && pathUrl[2]) {
              industryId = pathUrl[2];
            }
          }
          const payload = {
            industry_id: industryId,
            subindustry_id: [this.selectedCluster || -1],
            region_id: [-1],
            country_id: [],
            market_cap: [-1],
          };
          this.megaMenuService.loadFiltersAndUpdateStore(payload);
        }
      });
  }

  /**
   * Handler when user hovers on a sector name in mega menu.
   * @param {SectorDetails} sector The details of sector that is being hovered.
   */
  onSectorHover(sector: SectorDetails) {
    this.activeSector = sector.sectorId;
    this.selectedIndustryCount =
      this.sectorToIndustries[this.activeSector].length;
  }

  resetSectorSelection() {
    this.isAlreadyScrolled = false;
    this.activeSector = this.selectedSector;
  }

  ngOnDestroy() {
    if (this.sectorIndustryStoreSubscriber) {
      this.sectorIndustryStoreSubscriber.unsubscribe();
    }
    if (this.sectorIndustryStoreCompaniesDataSubscriber) {
      this.sectorIndustryStoreCompaniesDataSubscriber.unsubscribe();
    }
    if (this.companyFilterSubscriber) {
      this.companyFilterSubscriber.unsubscribe();
    }
    if (this.routeParamSubscriber) {
      this.routeParamSubscriber.unsubscribe();
    }
    if (this.userIndustrySettingsSubscriber) {
      this.userIndustrySettingsSubscriber.unsubscribe();
    }
  }

  ngAfterViewChecked() {
    if (!this.isAlreadyScrolled) {
      const elm = document.getElementsByClassName("selected-industry");
      if (elm && elm.length) {
        elm[0].scrollIntoView();
        this.isAlreadyScrolled = true;
      }
    }
  }

  makePayload() {
    // Remove R from region ids
    // and C from country ids
    let countries: Array<string | number> = Array.from([
      ...this.otherSelected.countries,
    ]); //, ...this.popularSelected])),
    countries = countries.map((country) =>
      Number((country as string).replace("C", "")),
    );
    let selectedRegions = [];
    if (this.allGeography.selected) {
      selectedRegions = [this.allGeography.key];
    } else {
      selectedRegions = Array.from(this.otherSelected.regions || []);
    }
    selectedRegions = selectedRegions.map((region) =>
      Number(region.replace("R", "")),
    );

    return {
      industry_id: this.selectedIndustry,
      subindustry_id: [this.selectedCluster],
      region_id: selectedRegions,
      country_id: countries,
      market_cap: this.selectedMarketCaps,
    };
  }

  loadIndustryFilterData(params) {
    // this.isMegaMenuReady = false;
    this.disableApply = true;
    if (this.companyFilterSubscriber) {
      this.companyFilterSubscriber.unsubscribe();
    }
    this.companyFilterSubscriber = this.dataService
      .getCompaniesFilter(params)
      .subscribe((data: any) => {
        const obj = this.megaMenuService.setSelectedValues(data);
        this.latestClusterDetails = obj ? { ...obj } : obj;
        this.error = obj.error;
        this.allGeography = obj.allGeography;
        this.popularGeography = obj.popularGeography;
        this.otherGeographies = obj.otherGeographies;
        this.popularSelected = obj.popularSelected;
        this.otherSelected = obj.otherSelected;
        this.marketCaps = obj.marketCaps;
        this.selectedMarketCaps = obj.selectedMarketCaps;
        this.clusters = obj.clusters;
        this.selectedCluster = obj.selectedCluster;
        this.selectedCompanyCount = obj.selectedCompanyCount;
        this.isMegaMenuReady = true;
        this.disableApply = false;
      });
  }

  onIndustrySelect(industryId) {
    this.loadIndustryFilterData({ industry_id: industryId });
    this.selectedIndustry = industryId;
    this.selectedSector = this.activeSector;
  }

  onClusterChange(clusterId) {
    this.selectedCluster = clusterId;
    this.loadIndustryFilterData(this.makePayload());
  }

  onMarketCapChange(marketCapIds) {
    this.selectedMarketCaps = marketCapIds;
    this.loadIndustryFilterData(this.makePayload());
  }

  onOtherValueChange(data) {
    this.allGeography.selected = data.isAllGeographySelected;
    this.otherSelected = {
      countries: new Set(data.countries),
      regions: new Set(data.regions),
    };
    this.loadIndustryFilterData(this.makePayload());
  }

  closeMegaMenu() {
    this.close.emit();
  }

  apply() {
    const userIndustrySettings =
      this.megaMenuService.createUserSettingsForFilter(
        this.selectedMarketCaps,
        this.selectedIndustry,
        this.otherSelected,
        this.selectedCluster,
        this.otherGeographies,
      );
    const obj = { ...this.latestClusterDetails };
    const currentPath = window.location.pathname;
    const pathUrl = currentPath.split("/");
    const pageType = pathUrl && pathUrl[3] ? pathUrl[3] : "kpis";
    let urlToNavigate = `/industry/${this.selectedIndustry}/${pageType}`;
    if (this.selectedCluster && this.selectedCluster !== -1) {
      urlToNavigate += `?subgroup=${this.selectedCluster}`;
    }

    if (this.initialSelectedIndustry !== this.selectedIndustry) {
      this.router.navigateByUrl(urlToNavigate);
      this.store.dispatch(
        new ChangeSectorIndustryAndSetClusterData({
          selectedIndustryId: this.selectedIndustry,
          error: obj.error,
          allGeography: obj.allGeography,
          popularGeography: obj.popularGeography,
          otherGeographies: obj.otherGeographies,
          popularSelected: obj.popularSelected,
          otherSelected: obj.otherSelected,
          marketCaps: obj.marketCaps,
          selectedMarketCaps: obj.selectedMarketCaps,
          clusters: obj.clusters,
          selectedCluster: obj.selectedCluster,
          selectedCompanyCount: obj.selectedCompanyCount,
          facetSelectionSettings: userIndustrySettings,
        }),
      );
    } else {
      if (this.selectedCluster !== this.initialSelectedCluser) {
        this.router.navigateByUrl(urlToNavigate);
      }
      this.store.dispatch(
        new SetClusterData({
          error: obj.error,
          allGeography: obj.allGeography,
          popularGeography: obj.popularGeography,
          otherGeographies: obj.otherGeographies,
          popularSelected: obj.popularSelected,
          otherSelected: obj.otherSelected,
          marketCaps: obj.marketCaps,
          selectedMarketCaps: obj.selectedMarketCaps,
          clusters: obj.clusters,
          selectedCluster: obj.selectedCluster,
          selectedCompanyCount: obj.selectedCompanyCount,
          facetSelectionSettings: userIndustrySettings,
        }),
      );
      this.store.dispatch(
        new SetReloadAll({
          reloadAll: true,
          facetSelectionSettings: userIndustrySettings,
        }),
      );
    }
    this.close.emit();
  }
}
